import { Button, Col, Form, Input, Row, Select, Typography } from 'antd';
import type { FormInstance } from 'antd';
import type {
  CreateFleetBody,
  CustomerDocumentType,
  Fleet,
  FleetBillingMode,
  FleetStatus,
  UpdateFleetBody,
} from '@goelectric/shared-types';
import {
  FLEET_DOCUMENT_TYPE_LABELS,
  FLEET_STATUS_LABELS,
} from '../utils/format';

/** Estado del formulario de flota (campos de `FleetCreateRequest`/`FleetUpdateRequest`). */
export interface FleetFormState {
  name: string;
  documentType: CustomerDocumentType;
  documentNumber: string;
  phone: string;
  email?: string;
  codigoFlota: string;
  billingMode: FleetBillingMode;
  status: FleetStatus;
}

const DOCUMENT_TYPE_OPTIONS = (['NIT', 'CC', 'CE', 'PPT'] as const).map((value) => ({
  value,
  label: FLEET_DOCUMENT_TYPE_LABELS[value],
}));

const STATUS_OPTIONS = (['ACTIVE', 'INACTIVE'] as const).map((value) => ({
  value,
  label: FLEET_STATUS_LABELS[value],
}));

/** Compone el cuerpo del alta con el contrato `FleetCreateRequest`. */
export function buildCreateFleetBody(values: FleetFormState): CreateFleetBody {
  const body: CreateFleetBody = {
    name: values.name.trim(),
    phone: values.phone.trim(),
    documentType: values.documentType,
    documentNumber: values.documentNumber.trim(),
    codigoFlota: values.codigoFlota.trim(),
    billingMode: 'UNIFIED',
    status: values.status,
  };
  const email = values.email?.trim();
  if (email) body.email = email;
  return body;
}

/** Compone el cuerpo de la edición parcial con el contrato `FleetUpdateRequest`. */
export function buildUpdateFleetBody(values: FleetFormState): UpdateFleetBody {
  const body: UpdateFleetBody = {
    name: values.name.trim(),
    phone: values.phone.trim(),
    documentType: values.documentType,
    documentNumber: values.documentNumber.trim(),
    codigoFlota: values.codigoFlota.trim(),
    billingMode: 'UNIFIED',
    status: values.status,
  };
  const email = values.email?.trim();
  if (email) body.email = email;
  return body;
}

/** Mapea una flota del contrato al estado del formulario (edición). */
export function fleetToFormState(fleet: Fleet): FleetFormState {
  return {
    name: fleet.name,
    documentType: fleet.documentType ?? 'NIT',
    documentNumber: fleet.documentNumber ?? '',
    phone: fleet.phone,
    email: fleet.email,
    codigoFlota: fleet.codigoFlota,
    billingMode: fleet.billingMode,
    status: fleet.status,
  };
}

export interface FleetFormProps {
  /** Recibe el cuerpo del contrato ya compuesto; la llamada a la API la hace la página. */
  onSubmit: (body: CreateFleetBody | UpdateFleetBody) => void;
  /** Alta o edición (cambia el cuerpo y el texto del botón). */
  mode?: 'create' | 'edit';
  /** Valores iniciales en edición. */
  initialValues?: FleetFormState;
  submitting?: boolean;
  /** Instancia del `Form` para que la página marque campos con errores del backend. */
  form?: FormInstance<FleetFormState>;
  submitLabel?: string;
}

/**
 * Alta/edición de flota (US-2.3, ADR 0047): identidad fiscal de la empresa, contacto,
 * código de negocio y estado. `billingMode` es **solo lectura UNIFIED** en #49 (la
 * facturación agrupada es una invariante del modelo, no una opción).
 */
export function FleetForm({
  onSubmit,
  mode = 'create',
  initialValues,
  submitting = false,
  form: formProp,
  submitLabel,
}: FleetFormProps) {
  const [internalForm] = Form.useForm<FleetFormState>();
  const form = formProp ?? internalForm;
  const isEdit = mode === 'edit';

  const handleFinish = (values: FleetFormState) => {
    onSubmit(isEdit ? buildUpdateFleetBody(values) : buildCreateFleetBody(values));
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{
        documentType: 'NIT',
        status: 'ACTIVE',
        billingMode: 'UNIFIED',
        ...initialValues,
      }}
      autoComplete="off"
    >
      <Row gutter={16}>
        <Col span={16}>
          <Form.Item
            label="Nombre de la flota"
            name="name"
            rules={[{ required: true, message: 'Ingresa el nombre de la empresa.' }]}
          >
            <Input placeholder="Ej. Transportes Andes S.A." />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Código de flota"
            name="codigoFlota"
            extra="Etiqueta de negocio; no es el UID de la tarjeta RFID."
            rules={[{ required: true, message: 'Ingresa el código de la flota.' }]}
          >
            <Input
              placeholder="Ej. ANDES"
              style={{ fontFamily: 'var(--ge-font-mono)' }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Tipo de documento"
            name="documentType"
            rules={[{ required: true, message: 'Selecciona el tipo de documento.' }]}
          >
            <Select<CustomerDocumentType>
              placeholder="Selecciona el tipo"
              options={DOCUMENT_TYPE_OPTIONS}
            />
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item
            label="Número de documento"
            name="documentNumber"
            rules={[{ required: true, message: 'Ingresa el número de documento.' }]}
          >
            <Input placeholder="Ej. 900123456" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Teléfono"
            name="phone"
            rules={[{ required: true, message: 'Ingresa el teléfono de contacto.' }]}
          >
            <Input placeholder="Ej. +573001234567" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Email"
            name="email"
            rules={[{ type: 'email', message: 'Ingresa un email válido.' }]}
          >
            <Input placeholder="Ej. flotas@empresa.co" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Modo de facturación"
            name="billingMode"
            extra="La Cuenta Maestra agrupa el saldo y la facturación de la flota."
          >
            <Input disabled aria-label="Modo de facturación (solo lectura)" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Estado"
            name="status"
            rules={[{ required: true, message: 'Selecciona el estado de la flota.' }]}
          >
            <Select<FleetStatus> options={STATUS_OPTIONS} />
          </Form.Item>
        </Col>
      </Row>

      <Typography.Text type="secondary">
        {isEdit
          ? 'Los cambios se aplican a la ficha de la flota y a su Cuenta Maestra.'
          : 'El alta crea la Cuenta Maestra (cliente FLOTA de plataforma) en estado activo.'}
      </Typography.Text>

      <Form.Item style={{ marginTop: 16, marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" loading={submitting}>
          {submitLabel ?? (isEdit ? 'Guardar cambios' : 'Crear flota')}
        </Button>
      </Form.Item>
    </Form>
  );
}
